import { useEffect, useRef, useState } from "react";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import TextInputElementComponent from "../../general/elements/inputElements/TextInputElementComponent";
import { useLocation, useNavigate } from "react-router-dom";
import "../../../styles/layouts/header/_headerSearchComponent.scss";
import AlertService from "../../../services/alerts/alert.service";
import useDebounce from "../../../hooks/useDebounce";

export const HeaderSearchComponent = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [scanning, setScanning] = useState(false);

  // 0035: the typed value stays instant on local state; only the NAVIGATION is debounced.
  // Previously every keystroke called navigate(), which re-rendered the whole route and pushed a
  // history entry per character — after typing five letters the user needed five Back presses to
  // leave. 0ms on an empty box so clearing returns home immediately.
  const debouncedQuery = useDebounce(searchQuery, searchQuery ? 350 : 0);

  // Navigate only when the settled term actually CHANGES. A "skip the first run" flag is not
  // enough: StrictMode double-invokes effects in development, so the second invocation would sail
  // past the flag with an empty term and redirect the user home on every page load. Comparing
  // against the previous value is idempotent — re-running the effect with an unchanged term does
  // nothing, in development and in production alike.
  const previousQueryRef = useRef(debouncedQuery);

  useEffect(() => {
    const previousQuery = previousQueryRef.current;
    previousQueryRef.current = debouncedQuery;

    if (debouncedQuery === previousQuery) return;

    if (!debouncedQuery) {
      navigate("/");
      return;
    }

    // The first hop into a search is a push, so Back returns to wherever the user came from.
    // Every refinement after that replaces, keeping one history entry per search rather than one
    // per character.
    navigate(`/search?query=${encodeURIComponent(debouncedQuery)}`, { replace: pathname === "/search" });
    // pathname is read, not tracked: re-running on every route change would re-navigate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // 0035: Quagga is ~2.6MB of image-processing code and this component renders on every page for
  // every visitor, almost none of whom will ever scan a barcode. It is fetched on the first press
  // instead of shipping in the main bundle. The press already waits on a camera permission prompt,
  // so the extra fetch is not felt.
  const quaggaRef = useRef(null);

  const loadQuagga = async () => {
    if (!quaggaRef.current) {
      const module = await import("quagga");
      quaggaRef.current = module.default || module;
    }
    return quaggaRef.current;
  };

  const startScanning = async () => {
    setScanning(true);

    document.body.style.overflow = "hidden"; // 🔒 prevent scroll
    document.documentElement.style.overflow = "hidden";

    try {
      const Quagga = await loadQuagga();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      stream.getTracks().forEach((track) => track.stop());

      let scannerElement = document.getElementById("barcode-scanner");
      if (!scannerElement) {
        scannerElement = document.createElement("div");
        scannerElement.id = "barcode-scanner";
        scannerElement.className = "scanner-overlay";
        document.body.appendChild(scannerElement);
      }

      Quagga.init(
        {
          inputStream: {
            type: "LiveStream",
            constraints: { facingMode: "environment" },
            target: scannerElement,
          },
          decoder: { readers: ["ean_reader", "upc_reader", "code_128_reader"] },
          locate: true,
        },
        (err) => {
          if (err) {
            console.error("QuaggaJS Initialization Error:", err);
            alert("Camera initialization failed. Please check permissions.");
            setScanning(false);
            return;
          }
          Quagga.start();
        }
      );

      Quagga.onDetected((result) => {
        const barcode = result.codeResult.code;
        Quagga.stop();
        setScanning(false);
        setSearchQuery(barcode);
        // A scan is a deliberate one-shot action — navigate now rather than waiting on the
        // typing debounce. The effect above will land on the same URL and change nothing.
        navigate(`/search?query=${encodeURIComponent(barcode)}`, { replace: pathname === "/search" });
      });
    } catch (error) {
      console.error("Camera access error:", error);
      AlertService("לא נמצאה מצלמה זמינה", "אנה השתמש במכשיר בו המצלמה זמינה או וודא כי ישנם הרשאות למצלמה באתר");
      setScanning(false);
    }
  };

  const stopScanning = () => {
    document.body.style.overflow = ''; // 🔓 re-enable scroll
    document.documentElement.style.overflow = '';

    quaggaRef.current?.stop();
    setScanning(false);
    const scannerElement = document.getElementById("barcode-scanner");
    if (scannerElement) {
      scannerElement.remove();
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value); // instant; the effect above navigates once typing settles
  };

  const getSearchInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "searchQuery",
      value: searchQuery,
      placeholder: "חפש מוצר",
      onChange: handleSearch,
      icon: <i className="fa-solid fa-barcode" onClick={startScanning}></i>,
      customInputClass: "headerSearchInput",
      customDesignClass: "headerSearchContainer",
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  return (
    <div className={`headerSearchContainer`}>
      {getSearchInputElement()}
      {scanning && (
        <div className="scanner-overlay">
          <button className="close-scanner" onClick={stopScanning}>
            ✖
          </button>
          <div className="focus-frame">
            <div></div> {/* Needed to support bottom corners */}
          </div>
          <div id="barcode-scanner"></div>
        </div>
      )}
    </div>
  );
};

export default HeaderSearchComponent;
// import { useState } from "react";
// import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
// import TextInputElementComponent from "../../general/elements/inputElements/TextInputElementComponent";
// import { useNavigate } from "react-router-dom";
// import Quagga from "quagga";
// import "../../../styles/layouts/header/_headerSearchComponent.scss";
// import AlertService from "../../../services/alerts/alert.service";

// export const HeaderSearchComponent = () => {
//   const navigate = useNavigate();
//   const [searchQuery, setSearchQuery] = useState("");
//   const [scanning, setScanning] = useState(false);

//   const startScanning = async () => {
//     setScanning(true);

//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
//       stream.getTracks().forEach((track) => track.stop());

//       let scannerElement = document.getElementById("barcode-scanner");
//       if (!scannerElement) {
//         scannerElement = document.createElement("div");
//         scannerElement.id = "barcode-scanner";
//         scannerElement.className = "scanner-overlay";
//         document.body.appendChild(scannerElement);
//       }

//       Quagga.init(
//         {
//           inputStream: {
//             type: "LiveStream",
//             constraints: { facingMode: "environment" },
//             target: scannerElement,
//           },
//           decoder: { readers: ["ean_reader", "upc_reader", "code_128_reader"] },
//           locate: true,
//         },
//         (err) => {
//           if (err) {
//             console.error("QuaggaJS Initialization Error:", err);
//             alert("Camera initialization failed. Please check permissions.");
//             setScanning(false);
//             return;
//           }
//           Quagga.start();
//         }
//       );

//       Quagga.onDetected((result) => {
//         const barcode = result.codeResult.code;
//         Quagga.stop();
//         setScanning(false);
//         setSearchQuery(barcode);
//         navigate(`/search?query=${encodeURIComponent(barcode)}`);
//       });
//     } catch (error) {
//       console.error("Camera access error:", error);
//       AlertService('לא נמצאה מצלמה זמינה', 'אנה השתמש במכשיר בו המצלמה זמינה או וודא כי ישנם הרשאות למצלמה באתר')
//       setScanning(false);
//     }
//   };

//   const stopScanning = () => {
//     Quagga.stop();
//     setScanning(false);
//     const scannerElement = document.getElementById("barcode-scanner");
//     if (scannerElement) {
//       scannerElement.remove();
//     }
//   };

//   const handleSearch = (event) => {
//     const { value } = event.target;
//     setSearchQuery(value);
//     if (!value) {
//       navigate("/");
//       return;
//     }
//     navigate(`/search?query=${encodeURIComponent(value)}`);
//   };

//   const getSearchInputElement = () => {
//     const inputProps = {
//       ...textInputModel,
//       name: "searchQuery",
//       value: searchQuery,
//       placeholder: "חפש מוצר",
//       onChange: handleSearch,
//       icon: <i className="fa-solid fa-barcode" onClick={startScanning}></i>,
//       customInputClass: "headerSearchInput",
//       customDesignClass: "headerSearchContainer",
//     };
//     return <TextInputElementComponent props={inputProps} />;
//   };

//   return (
//     <div className={`headerSearchContainer`}>
//       {getSearchInputElement()}
//       {scanning && (
//         <div className="scanner-overlay">
//           <button className="close-scanner" onClick={stopScanning}>✖</button>
//           <div id="barcode-scanner"></div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default HeaderSearchComponent;
